/**
 * Pure presentation logic for the per-device "Active updates" card.
 *
 * Factored out of DeviceUpdateJobs.tsx so it can be unit-tested without a
 * React/render harness — the same reason contentSync.ts, deviceStateTimeline.ts
 * and sessionRawRows.ts exist beside their components.
 *
 * The reason this moved: a stalled OS download and a healthy one rendered
 * IDENTICALLY. Device …057 sat at a frozen `486608896 / 559475200` for three
 * days and the card cheerfully showed "Downloading — 464 MB / 534 MB (87 %)"
 * the whole time. Agent 1.9.0 publishes `retry` / `maxRetries` /
 * `nextRetryInSec` when a download cycle has failed and it is backing off;
 * these helpers turn that into something an operator can act on.
 */
import { osSuperseded } from '../lib/osVersion';
import {
  contentSyncStatus,
  type AppliedContent,
  type AssignedContent,
} from './contentSync';
import { fmtBytes, fmtDuration } from './format';

/** Only the fields these helpers read — keeps the module free of API types. */
export interface JobLike {
  status_details?: Record<string, string> | null;
}

// Friendly labels for the device-reported stage (statusDetails.phase from the
// Mender agent, or statusDetails.step from content_sync). Falls back to the raw
// value so a new phase shows up as itself rather than disappearing.
export const STAGE_LABEL: Record<string, string> = {
  downloading: 'Downloading',
  download: 'Downloading',
  verify: 'Verifying',
  verifying: 'Verifying',
  install: 'Installing',
  installing: 'Installing',
  rebooting: 'Rebooting',
  awaiting_user: 'Awaiting acceptance',
  deferred: 'Deferred to next power-on',
  content_sync: 'Syncing content',
};

export function stageOf(job: JobLike): string | null {
  const raw = job.status_details?.phase ?? job.status_details?.step ?? null;
  if (!raw) return null;
  return STAGE_LABEL[raw] ?? raw;
}

export interface DownloadState {
  label: string;
  /** Percent complete, or null when the agent could not learn the total. */
  pct: number | null;
  /** The agent has failed at least one cycle and is backing off. */
  retrying: boolean;
  retry?: number;
  maxRetries?: number;
  nextRetryInSec?: number;
}

function num(v: string | undefined): number | null {
  if (v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Download progress from the agent's statusDetails.
 *
 * Agents below 1.7.0 publish neither byte key → null → the caller falls back to
 * the plain stage line. Agents below 1.9.0 publish bytes but never `retry` →
 * `retrying: false` → exactly the pre-existing rendering. Both stay correct
 * without a version check.
 */
export function downloadProgress(job: JobLike): DownloadState | null {
  const d = job.status_details ?? {};
  if ((d.phase ?? null) !== 'downloading') return null;

  const done = num(d.downloadedBytes);
  if (done === null) return null;
  const total = num(d.totalBytes);

  const pct = total !== null && total > 0
    ? Math.min(100, Math.round((done / total) * 100))
    : null;
  const size = pct !== null
    ? `${fmtBytes(done)} / ${fmtBytes(total!)} (${pct} %)`
    : fmtBytes(done);

  const retry = num(d.retry);
  if (retry === null) {
    return { label: `Downloading — ${size}`, pct, retrying: false };
  }

  const maxRetries = num(d.maxRetries);
  const nextRetryInSec = num(d.nextRetryInSec);
  const attempt = maxRetries !== null ? `${retry}/${maxRetries}` : `${retry}`;
  const next = nextRetryInSec !== null
    ? ` · next attempt in ${fmtDuration(nextRetryInSec)}`
    : '';
  return {
    label: `Retrying ${attempt} — ${size}${next}`,
    pct,
    retrying: true,
    retry,
    maxRetries: maxRetries ?? undefined,
    nextRetryInSec: nextRetryInSec ?? undefined,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DE-276 — staged-but-not-applied terminal jobs.
//
// A content/release job legitimately ends SUCCEEDED having only STAGED its
// payload (applies at the next power-on). The card used to drop it at that
// moment — on 2026-09-01 device …076 staged multilang-2026-08 twice, kept
// serving factory content, and the card said "No updates in progress." the
// whole time. The backend now returns such jobs in `staged`; the helpers below
// decide VISIBILITY from device truth, because a completed execution's
// statusDetails can never be revised — a row keyed on the job alone would be
// permanent and mislead the other way (see STAGED_NOTE in contentSync.ts).

/** The fields of a staged job these helpers read. */
export interface StagedJobLike extends JobLike {
  kind: string;
  /** DE-318 — os/app/ucfw as the admin Lambda reports them (ReleaseParts). */
  parts?: { content: boolean; os?: string | null; app?: string | null; ucfw?: string | null } | null;
}

/**
 * Recover the deploy's `force` flag from the details alone: only the FORCED
 * apply path ever emits applySkipped/applyError (agent.py `_force_apply_release`)
 * — an unforced release stages silently and reports neither.
 */
export function stagedForce(details: Record<string, string> | null | undefined): boolean {
  return Boolean(details?.applySkipped || details?.applyError);
}

/** One operator-readable line for the staged row body. */
export function stagedStageLine(details: Record<string, string> | null | undefined): string {
  if (details?.applySkipped)
    return `Apply skipped (${details.applySkipped}) — applies at next power-on`;
  if (details?.applyError)
    return `Immediate apply failed (${details.applyError}) — applies at next power-on`;
  return 'Staged — applies at next power-on';
}

/**
 * Should a staged job still be shown? Device truth decides, never the job:
 *
 *  - DE-318: a release whose OS the device has since moved PAST (it runs a newer
 *    OS than the job names) is hidden. It can never apply — its OS part cannot
 *    match again and a coupled content part is tagged to that OS — and the
 *    shadow's release.source stays 'pending' for good, so that signal must not
 *    keep the row. Seen on …031 and dev: 2026.08.1 "staged" for 25 days while
 *    running a newer dev OS with the release's content long applied.
 *  - carries content, contentSyncStatus === 'pending' → the device reports a
 *    profile identity that DIFFERS from the assignment — demonstrably owed.
 *  - carries content, status 'unknown' → the device reports NO profile identity.
 *    On a factory device that is the normal pre-first-apply state (…076: staged
 *    managed content, all collections managed:false), so an unmanaged collection
 *    keeps the row; all-managed means we cannot verify — hide rather than risk a
 *    permanently stuck row (the STAGED_NOTE failure mode).
 *  - carries content that has applied ('in-sync' / 'factory'): the content part
 *    is not owed, whatever release.source says (DE-318). The row stays only for a
 *    release that still names another part — an app, a uC firmware, or an OS not
 *    running yet — and only while release.source says something is pending.
 *  - no content at all: shadow release.source === 'pending' keeps the row
 *    (staged app/ucfw parts have no content signal of their own).
 */
export function shouldShowStaged(
  job: StagedJobLike,
  applied: AppliedContent | null | undefined,
  assigned: AssignedContent | null | undefined,
  releaseSource: string | null | undefined,
  runningOs: string | null = null,
): boolean {
  const parts = job.parts;
  if (job.kind === 'release' && osSuperseded(parts?.os ?? null, runningOs)) return false;
  const carriesContent = job.kind === 'content' || (job.kind === 'release' && Boolean(parts?.content));
  if (!carriesContent) return releaseSource === 'pending';
  const status = contentSyncStatus(applied, assigned);
  if (status === 'pending') return true;
  if (status === 'unknown') {
    return Object.values(applied?.collections ?? {}).some((c) => c?.managed === false);
  }
  // in-sync (done) or factory (nothing assigned): the content part is not owed.
  if (job.kind !== 'release' || releaseSource !== 'pending') return false;
  const osOwed = Boolean(parts?.os) && parts?.os !== runningOs;
  return Boolean(parts?.app || parts?.ucfw) || osOwed;
}
